/**
 * Mesh AI-retry domain helpers (ENG-1185).
 *
 * The retry loop lives in the backend (contracts §10); the FE only reads the
 * `retry` block on the mesh DETAIL response (list rows carry just
 * `retry_root_mesh_id` — contracts §9.1) and the `/retry-chain` endpoint.
 * These helpers centralize the gating/derivation rules so the tree, the HITL
 * card, and the pollers agree on them.
 */
import type {
  MeshParams,
  MeshRetryBlock,
  MeshStatus,
  RetryDecisionSummary,
} from '@/api/generated/types.gen';
import type { AutoRetryValue } from '@/components/AutoRetryFieldGroup';

/** Opt-in cap options — contracts §1.1 `MeshAutoRetryConfig` (ge=1, le=3). */
export const MESH_AUTO_RETRY_MAX_ATTEMPT_OPTIONS = [1, 2, 3];
/** Contracts §1.1 `Field(default=2)`. */
export const DEFAULT_MESH_AUTO_RETRY_MAX_ATTEMPTS = 2;

/**
 * Structural equality for an `{enabled, max_attempts}` auto-retry config —
 * the no-op identity guard shared by the store setters (toolSlice
 * `setMeshAutoRetry`, simopsSlice `setAutoRetry` / `setDraftMeshAutoRetry`).
 */
export function sameAutoRetryConfig(
  a: AutoRetryValue | null,
  b: AutoRetryValue | null,
): boolean {
  return (
    a === b ||
    (a !== null &&
      b !== null &&
      a.enabled === b.enabled &&
      a.max_attempts === b.max_attempts)
  );
}

/** Contract bound for `max_attempts`, derived from the option list. */
const MIN_MESH_AUTO_RETRY_ATTEMPTS = Math.min(
  ...MESH_AUTO_RETRY_MAX_ATTEMPT_OPTIONS,
);
const MAX_MESH_AUTO_RETRY_ATTEMPTS = Math.max(
  ...MESH_AUTO_RETRY_MAX_ATTEMPT_OPTIONS,
);

/**
 * How "off" is spelled in `MESH_SETTINGS`.
 *
 * NOT `null`, which is how the store spells it. AI retry shipped defaulting
 * off and persisted that as `null` (ENG-1185), so a stored `null` cannot tell
 * "the old default wrote this" from "a person un-ticked it" — and reading it
 * as off would have undone the ENG-2012 promotion for every existing browser.
 * Spelling a real choice differently is what retired the separate marker key
 * that used to carry that distinction.
 *
 * `max_attempts` is inert here — the decoder never looks at it on an off value
 * — but `AutoRetryValue` requires it, and an un-tick discards the user's count
 * anyway (`AutoRetryFieldGroup`).
 */
export const MESH_AUTO_RETRY_OFF: AutoRetryValue = {
  enabled: false,
  max_attempts: DEFAULT_MESH_AUTO_RETRY_MAX_ATTEMPTS,
};

/**
 * Encode a store value for `MESH_SETTINGS`. The inverse of
 * `readPersistedMeshAutoRetry` — the two are a pair, and a round-trip test
 * holds them together, because writing a bare `null` here would silently
 * reinstate ENG-2066 with every other test still green.
 */
export function writePersistedMeshAutoRetry(
  value: AutoRetryValue | null,
): AutoRetryValue {
  return value ?? MESH_AUTO_RETRY_OFF;
}

/**
 * Decode what this browser recorded about AI retry. Three states, all carried
 * by the stored value itself — there is no companion marker key:
 *
 * - `undefined` — no choice recorded. A fresh browser, a pre-flip `null`
 *   (see `MESH_AUTO_RETRY_OFF`), or a value too corrupt to read. Keep the
 *   store default, which is now on (ENG-2012): the safe end of the mistake is
 *   leaving the feature on for someone who never turned it off.
 * - `null` — the user un-ticked the box. Only an explicit `enabled: false`
 *   says this, so a foreign or half-written object cannot turn the feature off
 *   behind the user's back.
 * - an object — AI retry is on, `max_attempts` coerced into the contract bound
 *   (contracts §1.1) from the named constants, because stale storage may hold
 *   anything and a future cap change must not silently squash it.
 */
export function readPersistedMeshAutoRetry(
  stored: unknown,
): AutoRetryValue | null | undefined {
  if (stored == null || typeof stored !== 'object') return undefined;
  const { enabled, max_attempts } = stored as Partial<AutoRetryValue>;
  if (enabled === false) return null;
  if (enabled !== true) return undefined;
  const raw = Number(max_attempts);
  const max = Number.isFinite(raw)
    ? Math.min(
        MAX_MESH_AUTO_RETRY_ATTEMPTS,
        Math.max(MIN_MESH_AUTO_RETRY_ATTEMPTS, Math.round(raw)),
      )
    : DEFAULT_MESH_AUTO_RETRY_MAX_ATTEMPTS;
  return { enabled: true, max_attempts: max };
}

/**
 * Decision window sized to the real worst case: SQS hop + 60 s advisor read
 * timeout + dispatch txn — NOT ten minutes. A decision landing later (deep
 * SQS backlog, kill-switch flip) surfaces on the next focus-refetch instead.
 */
export const RETRY_DECISION_POLL_WINDOW_MS = 2 * 60_000;
/**
 * Cadence while only waiting for a decision entry (a terminal mesh isn't
 * "active" — the 2 s status poll would be poll spam).
 */
export const RETRY_DECISION_POLL_INTERVAL_MS = 10_000;
/**
 * Age (by the SERVER stamp) past which a terminal row is considered ancient
 * and never worth polling — opening a mesh that died last week must not
 * start a 2 min poll. Deliberately an hour rather than the 2 min window:
 * the precise window is client-anchored (see `terminalFirstSeenAt`), so this
 * gate only has to be loose enough to survive ordinary clock skew while
 * still rejecting rows that are unambiguously old.
 */
const RETRY_DECISION_STALE_AFTER_MS = 60 * 60_000;

/**
 * Structural subset the helpers need — satisfied by both `MeshResponse`
 * (list rows: `retry` is always absent) and `MeshDetailResponse`.
 */
export interface RetryAwareMesh {
  id?: string;
  status: MeshStatus;
  updated_at: string;
  mesh_params?: MeshParams | null;
  retry_root_mesh_id?: string | null;
  retry?: MeshRetryBlock | null;
}

/**
 * Any retry involvement at all — gates the chain query + history section.
 * The `retry` block is DETAIL-only (contracts §9.1) — callers pass the mesh
 * DETAIL response; on a bare list row only the root-pointer arm can fire.
 */
export function hasRetryInvolvement(mesh: RetryAwareMesh): boolean {
  return (
    mesh.retry_root_mesh_id != null ||
    (mesh.retry?.decisions?.length ?? 0) > 0 ||
    mesh.retry?.superseded_by_mesh_id != null ||
    mesh.retry?.retry_of_mesh_id != null
  );
}

/**
 * COMPLETED mesh known (client-side) to be below the quality policy: any
 * decision on it carries `failure_signature === 'quality_below_policy'`.
 * Gates the manual retry button on completed meshes (contracts §6.2 —
 * below-policy is retry-eligible; the backend manual path accepts COMPLETED
 * rows). Deliberate v1 narrowing: a below-policy mesh whose kill-switch ack
 * wrote no entry has no client-side signal and thus no button (LLD risk 11).
 */
export function hasBelowPolicySignal(mesh: RetryAwareMesh): boolean {
  return (mesh.retry?.decisions ?? []).some(
    (d) => d.failure_signature === 'quality_below_policy',
  );
}

/**
 * The AI's verdict on one retry chain, as the mesh tree draws it.
 *
 * Four states, because those are the four things a user does something
 * different about: nothing (leave it alone), the AI is working (wait), the AI
 * wants you (look), the AI gave up (take over). `null` is the common case —
 * a mesh nobody retried has nothing to say.
 */
export type MeshRetryMark =
  | { kind: 'needs-attention' }
  | { kind: 'retrying'; attempt: number }
  | { kind: 'recovered'; attempt: number }
  // `aiAttempts` is how many times the AI TRIED — the chain length minus the
  // user's own original run, so "the AI tried once" is not rendered as twice.
  | { kind: 'stopped'; aiAttempts: number };

/**
 * Which retry chain a row belongs to. Retry attempts carry a
 * `retry_root_mesh_id` denormalized to the chain root (contracts §1.1), so the
 * root keys on itself. Deliberately NOT the same grouping as
 * `groupMeshStacks`, which also walks `previous_mesh_id`: a stack can hold
 * refinements that were never part of any retry, and marking those as "the AI
 * fixed it" would be a lie.
 */
export function retryChainKey(mesh: RetryAwareMesh & { id: string }): string {
  return mesh.retry_root_mesh_id ?? mesh.id;
}

/**
 * Chain members in the order they ran. `created_at` is the only ordering the
 * list rows carry — `attempt_number` is on the detail block and the chain
 * endpoint, neither of which the tree fetches.
 *
 * So an attempt number here is a position within the rows the caller HAS. The
 * tree's list is capped at 100 rows per lineage, and a chain whose earliest
 * members fell outside that page would number its survivors from 1. Only
 * reachable on a geometry with more than 100 meshes; the switcher, which reads
 * the chain endpoint, carries the true `attempt_number`.
 */
function inRunOrder<T extends MarkAwareMesh>(chain: readonly T[]): T[] {
  return [...chain].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime() ||
      a.id.localeCompare(b.id),
  );
}

/** The list-row fields a mark is derived from. */
export interface MarkAwareMesh extends RetryAwareMesh {
  id: string;
  created_at: string;
  retry_needs_attention?: boolean;
  quality_policy_pass?: boolean | null;
}

/** Statuses that mean a run is still going — mirrors `ACTIVE_MESH_STATUSES`,
 * spelled here so this pure module does not reach into a hook. */
const RUNNING: ReadonlySet<MeshStatus> = new Set<MeshStatus>([
  'pending',
  'meshing',
  'recombining',
]);

/**
 * One chain's mark, or null when there is nothing worth saying.
 *
 * Order matters: a waiting proposal outranks everything, because it is the
 * only state where the loop has stopped and is waiting on a person. A chain of
 * one that nobody retried gets no mark at all — a badge on every ordinary mesh
 * is a badge nobody reads.
 */
export function meshRetryMark(
  chain: readonly MarkAwareMesh[],
): MeshRetryMark | null {
  if (chain.length === 0) return null;
  if (chain.some((m) => m.retry_needs_attention)) {
    return { kind: 'needs-attention' };
  }
  const ordered = inRunOrder(chain);
  // A chain of one is just a mesh nobody retried — no mark, in any status.
  // This guard must come BEFORE the running check: an ordinary mesh is
  // `meshing` for its whole run, and reading that as "the AI is running
  // attempt 1" put an AI badge on every submit, including one from a user who
  // had deliberately turned auto-retry off.
  if (ordered.length < 2) return null;
  const runningIndex = ordered.findIndex((m) => RUNNING.has(m.status));
  if (runningIndex >= 0) return { kind: 'retrying', attempt: runningIndex + 1 };
  const last = ordered[ordered.length - 1];
  // `quality_policy_pass` is null on a row the quality stage never judged;
  // that is not evidence of failure, so it reads as recovered.
  if (last.status === 'completed' && last.quality_policy_pass !== false) {
    return { kind: 'recovered', attempt: ordered.length };
  }
  return { kind: 'stopped', aiAttempts: ordered.length - 1 };
}

/** Per-geometry tallies for the rolled-up mark on a geometry row. */
export interface MeshRetryRollup {
  recovered: number;
  needsAttention: number;
}

/**
 * Marks for every retry chain in a flat mesh list, keyed by
 * `retryChainKey`, plus the per-chain rollup counts.
 *
 * Derived entirely from rows the tree already fetched — the "needs you" bit
 * rides on the list row for exactly this reason, so drawing these marks costs
 * no requests at all.
 */
export function buildMeshRetryMarks(meshes: readonly MarkAwareMesh[]): {
  byChain: Map<string, MeshRetryMark>;
  /** Every mesh id → its chain key, built in the pass that groups them. */
  chainKeyByMeshId: Map<string, string>;
  /** Members per chain — the "is there anything to switch between" fact. */
  sizeByChain: Map<string, number>;
  rollup: MeshRetryRollup;
} {
  const chains = new Map<string, MarkAwareMesh[]>();
  const chainKeyByMeshId = new Map<string, string>();
  const rollup: MeshRetryRollup = { recovered: 0, needsAttention: 0 };
  for (const mesh of meshes) {
    const key = retryChainKey(mesh);
    chainKeyByMeshId.set(mesh.id, key);
    // Counted per MESH, not per chain: two meshes in one chain can each hold a
    // proposal, and a header reading "1 mesh needs you" would under-report what
    // the user actually has to work through. The chain-level mark is a display
    // decision; the count is a workload.
    if (mesh.retry_needs_attention) rollup.needsAttention += 1;
    const members = chains.get(key);
    if (members) members.push(mesh);
    else chains.set(key, [mesh]);
  }
  const byChain = new Map<string, MeshRetryMark>();
  const sizeByChain = new Map<string, number>();
  for (const [key, members] of chains) {
    sizeByChain.set(key, members.length);
    const mark = meshRetryMark(members);
    if (!mark) continue;
    byChain.set(key, mark);
    if (mark.kind === 'recovered') rollup.recovered += 1;
  }
  return { byChain, chainKeyByMeshId, sizeByChain, rollup };
}

/**
 * Newest `lifecycle == 'proposed'` decision on this mesh, else null — the
 * HITL card's subject.
 */
export function latestProposedDecision(
  mesh: RetryAwareMesh,
): RetryDecisionSummary | null {
  const proposed = (mesh.retry?.decisions ?? []).filter(
    (d) => d.lifecycle === 'proposed',
  );
  if (proposed.length === 0) return null;
  return proposed.reduce((a, b) =>
    new Date(b.created_at).getTime() >= new Date(a.created_at).getTime()
      ? b
      : a,
  );
}

/**
 * 'rule' = deterministic infra re-dispatch (adjustments empty AND sop_version
 * null — contracts §1.2 verdict table); 'llm' otherwise. Drives the
 * rule-vs-LLM provenance tag.
 */
export function decisionKind(d: RetryDecisionSummary): 'rule' | 'llm' {
  return (d.adjustments ?? []).length === 0 && d.sop_version == null
    ? 'rule'
    : 'llm';
}

/**
 * First time (client clock) each terminal mesh row was observed by this
 * client, keyed on `id:updated_at`. The poll window anchors HERE rather than
 * on the server-stamped `updated_at`: comparing a server timestamp to
 * `Date.now()` let a client clock ≥2 min ahead expire the window instantly
 * (decision-window polling never started), and a clock behind extend it.
 * Module-level so the three call sites (detail poller, list poller, tree
 * poller) share one window per mesh.
 */
const terminalFirstSeenAt = new Map<string, number>();

function firstObservedTerminalAt(key: string, now: number): number {
  const seen = terminalFirstSeenAt.get(key);
  if (seen !== undefined) return seen;
  // Opportunistic prune — an expired anchor can never poll again, so a long
  // session doesn't accumulate one entry per terminal mesh forever.
  if (terminalFirstSeenAt.size >= 64) {
    for (const [k, v] of terminalFirstSeenAt) {
      if (now - v >= RETRY_DECISION_POLL_WINDOW_MS) {
        terminalFirstSeenAt.delete(k);
      }
    }
  }
  terminalFirstSeenAt.set(key, now);
  return now;
}

/** Test-only: forget the client-side poll-window anchors. */
export function resetRetryDecisionPollAnchors(): void {
  terminalFirstSeenAt.clear();
}

/**
 * Keep polling a terminal mesh while an auto decision may still land (the
 * worker appends post-terminal): opted-in via `mesh_params.auto_retry`,
 * status failed|completed, no decisions yet, the row isn't ancient, and
 * within `RETRY_DECISION_POLL_WINDOW_MS` of this client FIRST observing the
 * terminal row.
 *
 * Two clocks, deliberately: the WINDOW is client-anchored (skew-immune —
 * comparing `Date.now()` to a server stamp let a client clock ≥2 min ahead
 * expire the window before it opened), while `updated_at` is kept only as a
 * loose staleness gate so opening a long-dead mesh doesn't start a pointless
 * 2 min poll. Neither clock is trusted for what the other measures.
 */
export function shouldPollForRetryDecision(
  mesh: RetryAwareMesh,
  now: number = Date.now(),
): boolean {
  if (!mesh.mesh_params?.auto_retry?.enabled) return false;
  if (mesh.status !== 'failed' && mesh.status !== 'completed') return false;
  // At most one auto decision per input mesh (contracts §1.3) — once ANY
  // decision exists the wait is over.
  if ((mesh.retry?.decisions?.length ?? 0) > 0) return false;
  // Coarse ancient-row gate (server clock). An unparseable stamp is not a
  // reason to refuse — fall through to the client-anchored window.
  const updatedAt = new Date(mesh.updated_at).getTime();
  if (
    !Number.isNaN(updatedAt) &&
    now - updatedAt >= RETRY_DECISION_STALE_AFTER_MS
  ) {
    return false;
  }
  // `updated_at` in the key retires the anchor when the row itself changes
  // (a decision append bumps it — and also fails the decisions gate above).
  const firstSeen = firstObservedTerminalAt(
    `${mesh.id ?? ''}:${mesh.updated_at}`,
    now,
  );
  return now - firstSeen < RETRY_DECISION_POLL_WINDOW_MS;
}
